// ============================================
// COMPANY LOGIN PAGE - WITH TURNSTILE + i18n
// ============================================

import React from 'react';
import { createFileRoute, useNavigate, Link, redirect } from '@tanstack/react-router';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Loader2, AlertCircle, Eye, EyeOff, Mail } from 'lucide-react';
import toast from 'react-hot-toast';
import { useLogin } from '@/hooks';
import { useAuthStore } from '@/store/auth';
import { useEffect, useState } from 'react';
import { useTranslation } from '@crew-at-sea/i18n';
import { authApi } from '@/services/api';

export const Route = createFileRoute('/login')({
  beforeLoad: ({ context }: { context: any }) => {
    if (context.auth?.isAuthenticated) {
      throw redirect({ to: '/' });
    }
  },
  component: LoginPage,
});

const loginSchema = z.object({
  email: z.string().email('Geçerli bir e-posta adresi giriniz'),
  password: z.string().min(6, 'Şifre en az 6 karakter olmalıdır'),
});

type LoginFormData = z.infer<typeof loginSchema>;

function LoginPage() {
  const { t } = useTranslation('auth');
  const navigate = useNavigate();
  const { isAuthenticated } = useAuthStore();
  const [loginError, setLoginError] = useState<string>('');
  const [showPassword, setShowPassword] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const loginMutation = useLogin();

  const {
    register,
    handleSubmit,
    getValues,
    formState: { errors },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
  });

  const handleResendVerification = async () => {
    const email = getValues('email');
    if (!email) {
      toast.error('Lütfen önce e-posta adresinizi girin.');
      return;
    }
    
    setIsResending(true);
    try {
      const response = await authApi.resendVerification({ email });
      const msg = response.message || 'Doğrulama e-postası başarıyla gönderildi.';
      toast.success(msg);
    } catch (err: any) {
      toast.error(err?.message || 'Doğrulama e-postası gönderilemedi.');
    } finally {
      setIsResending(false);
    }
  };

  useEffect(() => {
    if (isAuthenticated) {
      navigate({ to: '/' });
    }
  }, [isAuthenticated, navigate]);

  // Honeypot state — bot tespiti
  const honeypotRef = React.useRef<HTMLInputElement>(null);

  const onSubmit = (data: LoginFormData) => {
    setLoginError('');
    // Honeypot kontrolü — bot tespiti
    if (honeypotRef.current?.value) return;
    loginMutation.mutate(data, {
      onError: (error: any) => {
        // mutationFn zaten Türkçe mesaj fırlatıyor
        setLoginError(error?.message || 'Giriş yapılamadı. Lütfen bilgilerinizi kontrol edin.');
      },
    });
  };

  return (
    <div 
      className="min-h-screen flex items-center justify-center px-4 relative"
      style={{
        backgroundImage: `url('/login-bg.webp')`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat'
      }}
    >
      <div className="absolute inset-0 bg-gradient-to-br from-blue-900/85 to-blue-700/80" />
      
      <div className="w-full max-w-md relative z-10">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-white/10 backdrop-blur-sm mb-4">
            <img src="/favicon.svg" alt="Crew At Sea" className="w-10 h-10 brightness-0 invert" />
          </div>
          <h1 className="text-3xl font-bold text-white">Crew At Sea</h1>
          <p className="text-blue-200 mt-1">{t('panelSubtitle.company')}</p>
        </div>

        <div className="bg-white rounded-2xl shadow-xl p-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-6">{t('panelLoginTitle.company')}</h2>

          {loginError && (
            <div className="mb-4 p-4 rounded-2xl bg-red-50/60 border border-red-100 backdrop-blur-sm shadow-sm flex flex-col gap-3 text-red-800 text-sm animate-in fade-in slide-in-from-top-2 duration-300">
              <div className="flex items-start gap-2.5">
                <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                <span className="font-medium leading-relaxed">{loginError}</span>
              </div>
            </div>
          )}

          {loginError && loginError.includes('doğrulamanızın') && (
            <div className="flex justify-center w-full mb-6 animate-in fade-in slide-in-from-top-2 duration-300">
              <button
                type="button"
                onClick={handleResendVerification}
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-xs font-semibold text-primary-700 bg-primary-50 hover:bg-primary-100 hover:text-primary-900 border border-primary-100 transition-all duration-200 active:scale-[0.98] disabled:opacity-50"
                disabled={isResending}
              >
                {isResending ? (
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                ) : (
                  <Mail className="w-3.5 h-3.5" />
                )}
                {isResending ? 'Gönderiliyor...' : 'Doğrulama e-postasını tekrar gönder'}
              </button>
            </div>
          )}

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                {t('login.email')}
              </label>
              <input
                id="email"
                type="email"
                className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${errors.email ? 'border-red-500' : 'border-gray-300'}`}
                placeholder="sirket@example.com"
                {...register('email')}
              />
              {errors.email && (
                <p className="mt-1 text-xs text-red-500">{errors.email.message}</p>
              )}
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
                {t('login.password')}
              </label>
              <div className="relative">
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  className={`w-full px-4 py-2 pr-10 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${errors.password ? 'border-red-500' : 'border-gray-300'}`}
                  placeholder="••••••••"
                  {...register('password')}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  tabIndex={-1}
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              {errors.password && (
                <p className="mt-1 text-xs text-red-500">{errors.password.message}</p>
              )}
            </div>

            <div className="text-right">
              <Link
                to="/forgot-password"
                className="text-sm text-blue-600 hover:text-blue-700"
              >
                {t('login.forgotPassword')}
              </Link>
            </div>

            <button
              type="submit"
              className="w-full bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              disabled={loginMutation.isPending}
            >
              {loginMutation.isPending ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  {t('login.submitting')}
                </>
              ) : (
                t('login.submit')
              )}
            </button>
            {/* Honeypot — botlar için gizli alan, kullanıcı görmez */}
            <input
              ref={honeypotRef}
              type="text"
              name="website"
              autoComplete="off"
              tabIndex={-1}
              aria-hidden="true"
              style={{ position: 'absolute', left: '-9999px', width: '1px', height: '1px', opacity: 0 }}
            />
          </form>

          <p className="mt-6 text-center text-sm text-gray-500">
            {t('login.noAccount')}{' '}
            <Link to="/register" className="text-blue-600 hover:text-blue-700 font-medium">
              {t('login.registerLink')}
            </Link>
          </p>
        </div>

        <p className="text-center text-blue-200 text-sm mt-8">
          {t('footer.copyright', { year: new Date().getFullYear() })}
        </p>
      </div>
    </div>
  );
}
